import posthog from "@/posthog";
import api from "@/services/api";
import { queryClient } from "@/services/query-client";
import {
  clearSentryImpersonation,
  prepareSentrySessionContext,
} from "@/services/sentry-context";
import { useStore } from "@/services/store";

import { queryOptions } from "@tanstack/react-query";
import { replace } from "react-router";

// The logged-in account of each role is one query on the existing
// `/<role>/signin_token` endpoint (ADR 0043). The route loaders below refresh
// it on every protected navigation; screens read it with
// `useQuery(sessionQuery(role)).data?.user` and write it with
// `queryClient.setQueryData(sessionQuery(role).queryKey, ...)`.
// A session the server refuses (4xx) is `{ user: null }`. A network error,
// a timeout or a 5xx keeps the account already loaded: a therapist coming
// back to the tab on a flaky connection must not be logged out.
export function sessionQuery(role) {
  return queryOptions({
    queryKey: [`/${role}/signin_token`],
    queryFn: async () => {
      try {
        const {
          ok,
          user,
          session_context: sessionContext,
        } = await api.get(`/${role}/signin_token`);
        if (!ok || !user) return { user: null, session_context: null };
        const sentryContextReady =
          await prepareSentrySessionContext(sessionContext);
        if (!sentryContextReady) return { user: null, session_context: null };
        return { user, session_context: sessionContext };
      } catch (error) {
        const status = Number(error?.status);
        if (status >= 400 && status < 500)
          return { user: null, session_context: null };
        throw error;
      }
    },
    meta: { source: "services/router-session", silent: true },
    // Only a loader or a write changes the session: screens read it from
    // the cache, and it must outlive the screens that read it.
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

// After a sign-in, a sign-up or an admin impersonation: the response already
// holds the account, no need to ask the server again.
export function startSession(role, { user, session_context: sessionContext }) {
  if (sessionContext?.mode !== "impersonation") clearSentryImpersonation();
  useStore.getState().setActiveRole(role);
  queryClient.setQueryData(sessionQuery(role).queryKey, {
    user,
    session_context: sessionContext || {
      mode: "normal",
      actor_admin_id: "",
      replay_enabled: true,
    },
  });
}

export function resetSession(role) {
  const isActiveSession = useStore.getState().activeRole === role;
  if (isActiveSession) clearSentryImpersonation();
  queryClient.setQueryData(sessionQuery(role).queryKey, {
    user: null,
    session_context: null,
  });
  if (!isActiveSession) return;
  useStore.getState().setActiveRole(null);
  posthog.reset();
}

export function setActiveSessionRole(role) {
  useStore.getState().setActiveRole(role || null);
}

export async function ensureSession(role, { refresh = false } = {}) {
  // A loader that cannot reach the server treats the visitor as logged out,
  // as before; the cached account stays for the next attempt.
  let session = null;
  try {
    session = await queryClient.fetchQuery({
      ...sessionQuery(role),
      staleTime: refresh ? 0 : Infinity,
    });
  } catch {
    return null;
  }
  if (!session.user && useStore.getState().activeRole === role)
    useStore.getState().setActiveRole(null);
  return session.user;
}

function getReturnPath(request) {
  const url = new URL(request.url);
  return `${url.pathname}${url.search}`;
}

function replaceWithReturnPath(basePath, request) {
  const next = getReturnPath(request);
  return replace(`${basePath}?redirect=${encodeURIComponent(next)}`);
}

function getTherapistStatusRedirect(therapist) {
  if (!therapist) return null;
  if (therapist.status === "draft" || therapist.status === "uncomplete") {
    return "/auth/therapist/register/complete";
  }
  if (therapist.status === "pending") {
    return "/auth/therapist/confirmation";
  }
  if (
    therapist.status === "active" &&
    !["basic", "premium"].includes(therapist.plan)
  ) {
    return "/therapist/account";
  }
  return "/therapist/agenda";
}

export async function customerGuestLoader() {
  setActiveSessionRole("customer");
  const customer = await ensureSession("customer", { refresh: true });
  if (!customer) return null;
  throw replace("/customer/bookings");
}

export async function therapistGuestLoader({ request } = {}) {
  setActiveSessionRole("therapist");
  const therapist = await ensureSession("therapist", { refresh: true });
  if (!therapist) return null;
  const url = request ? new URL(request.url) : null;
  if (
    url?.pathname === "/auth/therapist/register" &&
    url.searchParams.get("ref")
  ) {
    throw replace("/therapist/subscription?referral=already_registered");
  }
  throw replace(getTherapistStatusRedirect(therapist));
}

export async function therapistRegistrationLoader() {
  setActiveSessionRole("therapist");
  const therapist = await ensureSession("therapist", { refresh: true });
  if (!therapist) throw replace("/auth/therapist/login");
  if (therapist.status === "draft" || therapist.status === "uncomplete") {
    return null;
  }
  throw replace(getTherapistStatusRedirect(therapist));
}

export async function therapistConfirmationLoader({ request }) {
  setActiveSessionRole("therapist");
  const therapist = await ensureSession("therapist", { refresh: true });
  if (!therapist) {
    const url = new URL(request.url);
    if (url.searchParams.get("submitted") === "1") return null;
    throw replace("/auth/therapist/login");
  }
  if (therapist.status === "pending") return null;
  if (therapist.status === "draft" || therapist.status === "uncomplete") {
    throw replace("/auth/therapist/register/complete");
  }
  throw replace(getTherapistStatusRedirect(therapist));
}

export async function therapistEntryLoader() {
  setActiveSessionRole("therapist");
  const therapist = await ensureSession("therapist", { refresh: true });
  if (!therapist) return null;
  throw replace(getTherapistStatusRedirect(therapist));
}

export async function publicLayoutLoader() {
  const customer = await ensureSession("customer", { refresh: true });
  const therapist = await ensureSession("therapist", { refresh: true });
  const activeRole = useStore.getState().activeRole;
  if (activeRole === "customer" && customer) return null;
  if (activeRole === "therapist" && therapist) return null;
  if (customer) setActiveSessionRole("customer");
  if (!customer && therapist) setActiveSessionRole("therapist");
  if (!customer && !therapist) clearSentryImpersonation();
  return null;
}

export async function requireCustomerLoader({ request }) {
  setActiveSessionRole("customer");
  const customer = await ensureSession("customer", { refresh: true });
  if (customer) return null;
  throw replaceWithReturnPath("/auth", request);
}

export async function requireTherapistLoader({ request }) {
  setActiveSessionRole("therapist");
  const therapist = await ensureSession("therapist", { refresh: true });
  if (!therapist) {
    throw replaceWithReturnPath("/auth/therapist/login", request);
  }

  if (therapist.status === "draft" || therapist.status === "uncomplete") {
    throw replace("/auth/therapist/register/complete");
  }

  if (therapist.status === "pending") {
    throw replace("/auth/therapist/confirmation");
  }

  const url = new URL(request.url);
  const isAllowedFreePlanPath =
    url.pathname.startsWith("/therapist/subscription") ||
    url.pathname.startsWith("/therapist/settings") ||
    url.pathname.startsWith("/therapist/help") ||
    url.pathname.startsWith("/therapist/offices") ||
    url.pathname.startsWith("/therapist/offers") ||
    url.pathname.startsWith("/therapist/account");
  if (
    !["basic", "premium"].includes(therapist.plan) &&
    !isAllowedFreePlanPath
  ) {
    throw replace("/therapist/account");
  }

  return null;
}

export async function adminGuestLoader() {
  setActiveSessionRole("admin");
  const admin = await ensureSession("admin", { refresh: true });
  if (!admin) return null;
  throw replace("/admin");
}

export async function requireAdminLoader({ request }) {
  setActiveSessionRole("admin");
  const admin = await ensureSession("admin", { refresh: true });
  if (admin) return null;
  throw replaceWithReturnPath("/admin/auth/login", request);
}
